import { useEffect, useRef, useState } from 'react'
import { useGameStore } from '../../store/gameStore'
import { api } from '../../services/api'
import type { ResourceType } from '@territory-nexus/shared'
import { RESOURCE_CONFIG, RESOURCE_ORDER } from '@territory-nexus/shared'
import clsx from 'clsx'

// Max resource caps based on game constants
const RESOURCE_CAPS: Record<ResourceType, number> = {
  energy: 500,
  nanomaterials: 200,
  quantum: 100,
  darkMatter: 50,
}

interface GenerationRates {
  zonesCount: number
  totalRatePerHour: {
    energy: number
    nanomaterials: number
    quantum: number
    darkMatter: number
  }
}

function getVal(key: ResourceType, src: { energy: number; nanomaterials: number; quantum: number; darkMatter: number }): number {
  switch (key) {
    case 'energy': return src.energy
    case 'nanomaterials': return src.nanomaterials
    case 'quantum': return src.quantum
    case 'darkMatter': return src.darkMatter
  }
}

export default function ResourceBar() {
  const { resources, fetchResources } = useGameStore()
  const [rates, setRates] = useState<GenerationRates | null>(null)

  // Fetch rates
  const fetchRates = async () => {
    try {
      const res = await api.get<{ success: boolean; data: GenerationRates }>('/resources/rates')
      if (res.data.success) {
        setRates(res.data.data)
      }
    } catch (err) {
      console.error('Failed to fetch rates:', err)
    }
  }

  // Fetch resources and rates on mount
  useEffect(() => {
    fetchResources()
    fetchRates()
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchResources()
      fetchRates()
    }, 30000)
    return () => clearInterval(interval)
  }, [fetchResources])

  const getResourceRate = (key: ResourceType): number => {
    if (!rates) return 0
    switch (key) {
      case 'energy': return rates.totalRatePerHour.energy
      case 'nanomaterials': return rates.totalRatePerHour.nanomaterials
      case 'quantum': return rates.totalRatePerHour.quantum
      case 'darkMatter': return rates.totalRatePerHour.darkMatter
    }
  }

  // Track resource changes for visual feedback
  const prevResources = useRef({ energy: resources.energy, nanomaterials: resources.nanomaterials, quantum: resources.quantum, darkMatter: resources.darkMatter })
  const [deltas, setDeltas] = useState<Record<string, { amount: number; key: number }>>({})

  useEffect(() => {
    const prev = prevResources.current
    const newDeltas: Record<string, { amount: number; key: number }> = {}

    RESOURCE_ORDER.forEach((resKey) => {
      const prevVal = getVal(resKey, prev)
      const curVal = getVal(resKey, resources)
      if (curVal !== prevVal && prevVal !== 0) {
        newDeltas[resKey] = { amount: Math.round((curVal - prevVal) * 10) / 10, key: Date.now() + Math.random() }
      }
    })

    if (Object.keys(newDeltas).length > 0) {
      setDeltas(newDeltas)
      const timeout = setTimeout(() => setDeltas({}), 1200)
      prevResources.current = { energy: resources.energy, nanomaterials: resources.nanomaterials, quantum: resources.quantum, darkMatter: resources.darkMatter }
      return () => clearTimeout(timeout)
    }
    prevResources.current = { energy: resources.energy, nanomaterials: resources.nanomaterials, quantum: resources.quantum, darkMatter: resources.darkMatter }
  }, [resources.energy, resources.nanomaterials, resources.quantum, resources.darkMatter])

  const resourceList = RESOURCE_ORDER.map((key) => {
    const config = RESOURCE_CONFIG[key]
    return {
      key,
      name: config.name,
      shortName: config.shortName,
      symbol: config.symbol,
      value: getVal(key, resources),
      max: RESOURCE_CAPS[key],
      rate: getResourceRate(key),
      color: config.color,
      bgColor: config.bgColor,
    }
  })

  return (
    <div className="relative z-20 glass border-b border-white/10 px-2 py-1.5 sm:px-4 sm:py-2">
      <div className="flex items-center gap-2 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
          {resourceList.map((resource) => {
            const delta = deltas[resource.key]
            return (
              <div key={resource.key} className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
                <span className={`text-base sm:text-lg ${resource.color}`} style={{ filter: 'drop-shadow(0 0 3px currentColor)' }}>{resource.symbol}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-1 relative">
                    <span className={clsx(
                      'text-xs sm:text-sm font-semibold transition-colors duration-300',
                      delta?.amount && delta.amount > 0 ? 'text-emerald-300' :
                      delta?.amount && delta.amount < 0 ? 'text-red-300' :
                      'text-slate-200'
                    )}>
                      {resource.value}
                    </span>
                    {resource.rate > 0 && (
                      <span className="text-[9px] sm:text-[10px] text-emerald-400/70">+{resource.rate}/h</span>
                    )}
                    {/* Floating delta number */}
                    {delta && (
                      <span
                        key={delta.key}
                        className={clsx(
                          'absolute -top-3.5 left-0 text-[10px] font-bold resource-delta',
                          delta.amount > 0 ? 'text-emerald-400' : 'text-red-400'
                        )}
                      >
                        {delta.amount > 0 ? '+' : ''}{delta.amount}
                      </span>
                    )}
                  </div>
                  <div className="hidden sm:block text-[10px] text-slate-500 uppercase">{resource.name}</div>
                </div>
                <div className="hidden sm:block w-16 lg:w-24 h-1 bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${resource.bgColor}`}
                    style={{ width: `${Math.min((resource.value / resource.max) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
